import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { WarrantyClaimStatus } from '../../../../../../utils/warrantyClaimsEnums';

const REASON_MAX_LENGTH = 500;

/**
 * Modal component for changing warranty claim status from Rejected/Accepted
 * back to earlier workflow states (Draft / Return / KarsanReturn / WaitingForApproval).
 *
 * @param {boolean} visible - Modal visibility
 * @param {function} onHide - Function to hide modal
 * @param {function} onConfirm - (newStatus, reason) => Promise<void>
 * @param {number} currentStatus - Current claim status code (REJECTED or ACCEPTED)
 * @param {string} currentStatusName - Display name of current status
 * @param {string} claimNo - Display value for claim number
 * @param {boolean} isLoading - Loading state for submit
 */
const StatusChangeModal = ({ visible, onHide, onConfirm, currentStatus, currentStatusName = '', claimNo = '', isLoading = false }) => {
    const { th } = useLanguage();

    const [newStatus, setNewStatus] = useState(null);
    const [reason, setReason] = useState('');
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (visible) {
            setNewStatus(null);
            setReason('');
            setSubmitted(false);
        }
    }, [visible]);

    const statusOptions = useMemo(() => {
        return [
            { label: th('warrantyClaims.statusChange.statusOptions.draft'), value: WarrantyClaimStatus.DRAFT },
            { label: th('warrantyClaims.statusChange.statusOptions.return'), value: WarrantyClaimStatus.RETURNED },
            { label: th('warrantyClaims.statusChange.statusOptions.karsanReturn'), value: WarrantyClaimStatus.RETURNED_TO_KARSAN },
            { label: th('warrantyClaims.statusChange.statusOptions.waitingForApproval'), value: WarrantyClaimStatus.WAITING }
        ];
    }, [th]);

    const currentStatusSeverity = useMemo(() => {
        if (currentStatus === WarrantyClaimStatus.REJECTED) return 'danger';
        if (currentStatus === WarrantyClaimStatus.ACCEPTED) return 'success';
        return 'info';
    }, [currentStatus]);

    const handleSubmit = () => {
        setSubmitted(true);

        if (!newStatus) {
            return;
        }

        if (newStatus === currentStatus) {
            return;
        }

        confirmDialog({
            message: th('warrantyClaims.statusChange.confirmMessage'),
            header: th('warrantyClaims.statusChange.confirmHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('common.yes') || 'Evet',
            rejectLabel: th('common.no') || 'Hayır',
            acceptClassName: 'p-button-primary',
            rejectClassName: 'p-button-outlined',
            accept: () => {
                onConfirm(newStatus, reason ? reason.trim() : '');
            }
        });
    };

    const handleCancel = () => {
        setNewStatus(null);
        setReason('');
        setSubmitted(false);
        onHide();
    };

    const isSameStatusError = submitted && newStatus && newStatus === currentStatus;

    const renderFooter = () => (
        <div className="flex justify-content-end gap-2">
            <Button label={th('warrantyClaims.statusChange.cancel')} icon="pi pi-times" className="p-button-outlined" onClick={handleCancel} disabled={isLoading} />
            <Button label={th('warrantyClaims.statusChange.submit')} icon="pi pi-refresh" onClick={handleSubmit} loading={isLoading} disabled={isLoading} className="p-button-primary" />
        </div>
    );

    return (
        <>
            <ConfirmDialog />
            <Dialog header={th('warrantyClaims.statusChange.modalTitle')} visible={visible} style={{ width: '520px' }} modal footer={renderFooter()} onHide={handleCancel} closable={!isLoading}>
                <div className="p-3">
                    {claimNo && (
                        <div className="field mb-3">
                            <label className="font-bold block mb-2">{th('warrantyClaims.detail.form.warrantyClaimNo')}</label>
                            <span>{claimNo}</span>
                        </div>
                    )}

                    <div className="field mb-3">
                        <label className="font-bold block mb-2">{th('warrantyClaims.statusChange.currentStatus')}</label>
                        <Tag value={currentStatusName || '-'} severity={currentStatusSeverity} />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="newStatus" className="font-bold block mb-2">
                            {th('warrantyClaims.statusChange.newStatus')}
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <Dropdown
                            id="newStatus"
                            value={newStatus}
                            options={statusOptions}
                            onChange={(e) => setNewStatus(e.value)}
                            placeholder={th('warrantyClaims.statusChange.newStatusPlaceholder')}
                            className={submitted && (!newStatus || isSameStatusError) ? 'p-invalid w-full' : 'w-full'}
                            disabled={isLoading}
                        />
                        {submitted && !newStatus && <small className="p-error">{th('warrantyClaims.statusChange.errors.newStatusRequired')}</small>}
                        {isSameStatusError && <small className="p-error">{th('warrantyClaims.statusChange.errors.sameStatus')}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="reason" className="font-bold block mb-2">{th('warrantyClaims.statusChange.reason')}</label>
                        <InputTextarea
                            id="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={4}
                            cols={30}
                            maxLength={REASON_MAX_LENGTH}
                            className="w-full"
                            placeholder={th('warrantyClaims.statusChange.reasonPlaceholder')}
                            disabled={isLoading}
                        />
                        <small className="text-500 block mt-1">{(reason || '').length}/{REASON_MAX_LENGTH}</small>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default StatusChangeModal;
